import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Users, ArrowLeft, MapPin, Radio, Gift, Sparkles } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { VENUES, GROUPS } from "@/data/mockData";
import { fetchVenues, prizeEmoji } from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";

const STEPS = [5, 10, 20, 40];

export default function Search() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { joinGroup, profile } = useApp();
  const [tab, setTab] = useState(params.get("tab") === "groups" ? "groups" : "venues");
  const [stepIdx, setStepIdx] = useState([3]); // default 40 miles
  const [partners, setPartners] = useState([]);
  const maxDist = STEPS[stepIdx[0]];

  useEffect(() => {
    fetchVenues().then(setPartners).catch(() => {});
  }, []);

  const partnerVenues = partners.filter((v) => (v.distance || 0) <= maxDist);
  const venues = VENUES.filter((v) => v.distance <= maxDist);
  const groups = GROUPS.filter((g) => g.distance <= maxDist);

  const handleJoin = (g) => {
    const already = profile.groups.includes(g.name);
    joinGroup(g.name);
    toast.success(already ? `Back with ${g.name}!` : `You joined ${g.name}! 🎉`);
  };

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6 pt-4">
        <button data-testid="search-back-btn" onClick={() => navigate("/")} className="text-slate-400 p-1">
          <ArrowLeft strokeWidth={2.5} />
        </button>
        <h1 className="font-display text-3xl font-black text-slate-50">Near You</h1>
      </div>

      <div className="flex bg-slate-900 rounded-2xl p-1.5 mb-6 border border-slate-800">
        <button
          data-testid="search-tab-venues"
          onClick={() => setTab("venues")}
          className={`flex-1 py-3 rounded-xl font-bold text-sm no-tap-highlight ${tab === "venues" ? "bg-amber-400 text-slate-950" : "text-slate-400"}`}
        >
          Venues
        </button>
        <button
          data-testid="search-tab-groups"
          onClick={() => setTab("groups")}
          className={`flex-1 py-3 rounded-xl font-bold text-sm no-tap-highlight ${tab === "groups" ? "bg-pink-500 text-white" : "text-slate-400"}`}
        >
          Groups
        </button>
      </div>

      <div className="bg-slate-900 rounded-2xl p-5 mb-6 border border-slate-800">
        <div className="flex justify-between items-center mb-4">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Distance</span>
          <span data-testid="distance-value" className="font-display font-black text-2xl text-amber-400">{maxDist} mi</span>
        </div>
        <Slider
          data-testid="distance-slider"
          value={stepIdx}
          onValueChange={setStepIdx}
          min={0}
          max={3}
          step={1}
          className="py-2"
        />
        <div className="flex justify-between mt-2 text-[11px] font-bold text-slate-500">
          {STEPS.map((s) => <span key={s}>{s}</span>)}
        </div>
      </div>

      {tab === "venues" ? (
        <div className="space-y-4" data-testid="venue-list">
          {partnerVenues.length > 0 && (
            <div className="space-y-4" data-testid="partner-venue-list">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-purple-400 flex items-center gap-1.5">
                <Sparkles size={13} /> Partner venues
              </p>
              {partnerVenues.map((v, i) => (
                <motion.button
                  key={v.id}
                  data-testid={`partner-venue-${v.id}`}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate(`/venue/${v.id}`)}
                  className="w-full text-left bg-slate-900 rounded-2xl p-5 border border-purple-500/30 no-tap-highlight"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-display font-bold text-lg text-slate-50">{v.name}</h3>
                      <p className="text-slate-400 text-sm">{v.type}{v.location ? ` · ${v.location}` : ""}</p>
                    </div>
                    <span className="text-[10px] font-bold uppercase px-2.5 py-1 rounded-full bg-purple-500 text-white">Partner</span>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-sm text-slate-300">
                    <span className="flex items-center gap-1"><MapPin size={14} className="text-cyan-400" /> {v.distance} mi</span>
                  </div>
                  {v.prize?.title && (
                    <div className="mt-3 inline-flex items-center gap-1.5 bg-amber-400/10 border border-amber-400/30 rounded-full px-3 py-1 text-xs font-bold text-amber-300">
                      <Gift size={12} /> {prizeEmoji(v.prize.kind)} {v.prize.title}
                    </div>
                  )}
                </motion.button>
              ))}
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 pt-2">More nearby</p>
            </div>
          )}
          {venues.map((v, i) => (
            <motion.button
              key={v.id}
              data-testid={`venue-card-${v.id}`}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(`/setup?venue=${encodeURIComponent(v.name)}`)}
              className="w-full text-left bg-slate-900 rounded-2xl p-5 border border-slate-800 no-tap-highlight"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-display font-bold text-lg text-slate-50">{v.name}</h3>
                  <p className="text-slate-400 text-sm">{v.type}</p>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full ${v.tag === "Live Now" ? "bg-green-500 text-white" : "bg-slate-800 text-slate-300"}`}>
                  {v.tag}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-3 text-sm text-slate-300">
                <span className="flex items-center gap-1"><MapPin size={14} className="text-cyan-400" /> {v.distance} mi</span>
                <span className="flex items-center gap-1"><Star size={14} className="text-amber-400" /> {v.rating}</span>
                {v.live > 0 && <span className="flex items-center gap-1"><Radio size={14} className="text-pink-400" /> {v.live} playing</span>}
              </div>
            </motion.button>
          ))}
          {venues.length === 0 && <p className="text-slate-500 text-center py-8">No venues in range. Widen the slider!</p>}
        </div>
      ) : (
        <div className="space-y-4" data-testid="group-list">
          {groups.map((g, i) => {
            const joined = profile.groups.includes(g.name);
            return (
              <motion.div
                key={g.id}
                data-testid={`group-card-${g.id}`}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => navigate(`/group/${g.id}`)}
                role="button"
                className="bg-slate-900 rounded-2xl p-5 border border-slate-800 flex items-center gap-4 cursor-pointer no-tap-highlight"
              >
                <div className="text-4xl">{g.emoji}</div>
                <div className="flex-1">
                  <h3 className="font-display font-bold text-lg text-slate-50">{g.name}</h3>
                  <p className="text-slate-400 text-sm">{g.vibe}</p>
                  <p className="text-slate-500 text-xs mt-1 flex items-center gap-2">
                    <Users size={12} /> {g.members} members · {g.distance} mi
                  </p>
                </div>
                <motion.button
                  data-testid={`group-join-${g.id}`}
                  whileTap={{ scale: 0.92 }}
                  onClick={(e) => { e.stopPropagation(); handleJoin(g); }}
                  className={`px-4 py-2.5 rounded-xl font-bold text-sm no-tap-highlight ${joined ? "bg-slate-800 text-green-400" : "bg-pink-500 text-white"}`}
                >
                  {joined ? "Joined ✓" : "Join"}
                </motion.button>
              </motion.div>
            );
          })}
          {groups.length === 0 && <p className="text-slate-500 text-center py-8">No groups in range. Widen the slider!</p>}
        </div>
      )}
    </div>
  );
}
